import React, { useEffect, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import Header from '../Header';

ChartJS.register(ArcElement, Tooltip, Legend);

// Generate distinct colors dynamically
const generateColors = (count) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const hue = Math.floor((360 / count) * i);
    colors.push(`hsl(${hue}, 70%, 50%)`);
  }
  return colors;
};

const AllReports = () => {
  const [taskData, setTaskData] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [serviceData, setServiceData] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [taskRes, leadRes, ticketRes, serviceRes, planRes] = await Promise.all([
          fetch('https://apiaksharaconnect.aksharafinalytics.in/allTaskReports'),
          fetch('https://apiaksharaconnect.aksharafinalytics.in/lead-conversion'),
          fetch('https://apiaksharaconnect.aksharafinalytics.in/ticket-status'),
          fetch('https://apiaksharaconnect.aksharafinalytics.in/service-popularity'),
          fetch('https://apiaksharaconnect.aksharafinalytics.in/plan-popularity')
        ]);

        if (!taskRes.ok || !leadRes.ok || !ticketRes.ok || !serviceRes.ok || !planRes.ok) {
          throw new Error('Failed to fetch data from one or more endpoints');
        }

        const [taskJson, leadJson, ticketJson, serviceJson, planJson] = await Promise.all([
          taskRes.json(),
          leadRes.json(),
          ticketRes.json(),
          serviceRes.json(),
          planRes.json()
        ]);

        setTaskData(taskJson);
        setLeadData(leadJson);
        setTicketData(ticketJson);
        setServiceData(serviceJson);
        setPlanData(planJson);

      } catch (err) {
        console.error(err);
        setError(err.message);
      }
    };

    fetchData();
  }, []);

  // Chart Data
  const allTaskData = {
    labels: ['Open', 'In Progress', 'Closed'],
    datasets: [{
      data: [
        taskData?.open_count || 0,
        taskData?.in_progress_count || 0,
        taskData?.closed_count || 0
      ],
      backgroundColor: ['#f39c12', '#3498db', '#2ecc71'],
      borderWidth: 1
    }]
  };

  const leadConversionData = {
    labels: ['Leads', 'Users'],
    datasets: [{
      data: [
        (leadData?.total_leads || 0) - (leadData?.converted_leads || 0),
        leadData?.converted_leads || 0
      ],
      backgroundColor: ['#e74c3c', '#27ae60'],
      borderWidth: 1
    }]
  };

  const allTicketData = {
    labels: ['Open', 'In Progress', 'Closed'],
    datasets: [{
      data: [
        ticketData?.open || 0,
        ticketData?.In_progress || ticketData?.['in_progress'] || 0,
        ticketData?.closed || 0
      ],
      backgroundColor: ['#f39c12', '#3498db', '#2ecc71'],
      borderWidth: 1
    }]
  };

  const serviceLabels = Array.isArray(serviceData?.services)
    ? serviceData.services.map(s => s.service_name)
    : [];
  const serviceCounts = Array.isArray(serviceData?.services)
    ? serviceData.services.map(s => s.users_count)
    : [];

  const servicePopularityData = {
    labels: serviceLabels.length ? serviceLabels : ['No data'],
    datasets: [{
      data: serviceCounts.length ? serviceCounts : [1],
      backgroundColor: generateColors(serviceLabels.length || 1),
      borderWidth: 1
    }]
  };

  const planLabels = planData?.plans?.length > 0
  ? planData.plans.map(p => p.plan_name)
  : null;

const planCounts = planData?.plans?.length > 0
  ? planData.plans.map(p => p.users_count)
  : null;

  const planPopularityData = {
  labels: planLabels || ['No data'],
  datasets: [{
    data: planCounts || [1],
    backgroundColor: generateColors(planLabels?.length || 1),
    borderWidth: 1
  }]
};


  const commonOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: { size: 10 },
          generateLabels: (chart) => {
            const data = chart.data;
            return data.labels.map((label, i) => ({
              text: label.length > 25 ? label.slice(0, 25) + '…' : label,
              fillStyle: data.datasets[0].backgroundColor[i],
              index: i
            }));
          }
        }
      }
    }
  };

  return (
    <>
      <Header />

      <div className="container-fluid py-4">
        {error && <div className="alert alert-danger">{error}</div>}

        {(taskData && leadData && ticketData && serviceData && planData) ? (
          <div className="card shadow">
            <div className="card-header bg-primary text-white text-center">
              <h4 className="mb-0">All Reports</h4>
            </div>
            <div className="card-body">
              <div className="row justify-content-center text-center">

                {/* Tasks */}
                <div className="col-6 col-md-3 mb-4">
                  <h6><b>Task</b></h6>
                  <div style={{ minHeight: '200px' }}>
                    <Pie data={allTaskData} options={commonOptions} />
                  </div>
                </div>

                {/* Tickets */}
                <div className="col-6 col-md-3 mb-4">
                  <h6><b>Ticket</b></h6>
                  <div style={{ minHeight: '200px' }}>
                    <Pie data={allTicketData} options={commonOptions} />
                  </div>
                </div>

                {/* Leads */}
                <div className="col-6 col-md-3 mb-4">
                  <h6><b>Leads - Users</b></h6>
                  <div style={{ minHeight: '200px' }}>
                    <Pie data={leadConversionData} options={commonOptions} />
                  </div>
                </div>

                <hr/>
                {/* Services */}
                <div className="col-12 col-md-6 mb-4">
                  <h6><b> Service</b></h6>
                  <div style={{ minHeight: '400px' }}>
                    <Pie data={servicePopularityData} options={commonOptions} />
                  </div>
                </div>

                {/* Plans */}
                <div className="col-12 col-md-6 mb-4">
                  <h6><b>Plan </b></h6>
                  <div style={{ minHeight: '400px' }}>
                    <Pie data={planPopularityData} options={commonOptions} />
                  </div>
                </div>

              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-5">Loading charts...</div>
        )}
      </div>
    </>
  );
};

export default AllReports;
